import { useEffect, useState, type ReactNode } from 'react'
import type { StepperRowStatus } from '@/components/gcp/gcpHelpers'
import {
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationCircleIcon,
  MinusCircleIcon,
} from '@heroicons/react/24/outline'

interface GcpAnalyzerSectionProps {
  id: string
  title: string
  status: StepperRowStatus
  errorMessage?: string | null
  /** When true, section body starts expanded (also re-syncs when status becomes done/failed). */
  defaultOpen?: boolean
  children?: ReactNode
}

function StatusIcon({ status }: { status: StepperRowStatus }) {
  if (status === 'running') {
    return (
      <svg
        className="h-5 w-5 animate-spin text-brand-600"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    )
  }
  if (status === 'done') {
    return <CheckCircleIcon className="h-5 w-5 text-green-600" aria-hidden />
  }
  if (status === 'failed') {
    return <ExclamationCircleIcon className="h-5 w-5 text-red-600" aria-hidden />
  }
  return <MinusCircleIcon className="h-5 w-5 text-gray-300" aria-hidden />
}

function statusLabel(status: StepperRowStatus): string {
  switch (status) {
    case 'running':
      return 'Running'
    case 'done':
      return 'Done'
    case 'failed':
      return 'Failed'
    default:
      return 'Pending'
  }
}

export default function GcpAnalyzerSection({
  id,
  title,
  status,
  errorMessage,
  defaultOpen = false,
  children,
}: GcpAnalyzerSectionProps) {
  const [open, setOpen] = useState(defaultOpen)

  useEffect(() => {
    if (status === 'running') setOpen(true)
    else if (status === 'done' || status === 'failed') setOpen(defaultOpen)
  }, [status, defaultOpen])

  const canExpand = status === 'done' || status === 'failed' || status === 'running'

  return (
    <section
      id={id}
      className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
    >
      <button
        type="button"
        className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 disabled:cursor-default disabled:hover:bg-white"
        onClick={() => {
          if (canExpand) setOpen((value) => !value)
        }}
        aria-expanded={open}
        disabled={!canExpand}
      >
        <StatusIcon status={status} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <h2 className="container-title">{title}</h2>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{statusLabel(status)}</span>
              {canExpand ? (
                open ? (
                  <ChevronUpIcon className="h-4 w-4 text-gray-500" aria-hidden />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-gray-500" aria-hidden />
                )
              ) : null}
            </div>
          </div>
          {status === 'failed' && errorMessage && !open ? (
            <p className="mt-1 text-xs text-red-700">{errorMessage}</p>
          ) : null}
        </div>
      </button>

      {open ? (
        <div className="space-y-4 border-t border-gray-200 px-4 py-4">
          {status === 'running' ? (
            <p className="text-sm text-gray-500">Collecting data for this section…</p>
          ) : null}
          {status === 'failed' && errorMessage ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {errorMessage}
            </p>
          ) : null}
          {status === 'done' || (status === 'failed' && children) ? children : null}
        </div>
      ) : null}
    </section>
  )
}
